import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { GivingLinkDetailDialog } from '@/components/payments/giving-link-detail-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { ShareDialog } from '@/components/shared/share-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { buildGivingLinkUrl } from '@/lib/public-origins';
import { ONLINE_GIVING_SETTINGS_PATH } from '@/lib/online-giving-settings';
import { usePaymentConnection } from '@/lib/payment-connection';
import {
  hasPermission,
  isAdminOrOwner,
  PERMISSIONS,
  useCurrentMembership,
} from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatNumber } from '@/utils/format';

interface GivingLink {
  id: string;
  slug: string;
  title: string;
  allowedFundIds: string[];
  defaultFundId: string;
  defaultFundName: string | null;
  isActive: boolean;
  viewCount: number;
  giftCount: number;
  createdAt: string;
}

export const GivingLinksPanel = () => {
  const { t } = useTranslation('payments');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const church = useChurchStore((state) => state.church);
  const canManage = hasPermission(membership, PERMISSIONS.PAYMENTS_MANAGE_LINKS);
  const canConfigurePayments =
    isAdminOrOwner(membership) || hasPermission(membership, PERMISSIONS.PAYMENTS_CONFIGURE);
  const { isReady: paymentReady } = usePaymentConnection(canManage);

  const [shareLink, setShareLink] = useState<{ slug: string; title: string } | null>(null);
  const [detailLinkId, setDetailLinkId] = useState<string | null>(null);
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view');

  const openDetail = (linkId: string, mode: 'view' | 'edit') => {
    setDetailMode(mode);
    setDetailLinkId(linkId);
  };

  const list = useQuery({
    queryKey: ['payments', 'giving-links'],
    queryFn: () =>
      api.get<{ data: GivingLink[]; page: OffsetPage }>('/payments/giving-links', {
        query: { page: 1, pageSize: 50, includeInactive: 'true' },
      }),
    placeholderData: keepPreviousData,
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<GivingLink>(`/payments/giving-links/${id}`, { isActive }),
    onSuccess: (_link, variables) => {
      toast.success(variables.isActive ? t('links.resumed') : t('links.paused'));
      void queryClient.invalidateQueries({ queryKey: ['payments', 'giving-links'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<GivingLink>[] = useMemo(
    () => [
      {
        id: 'title',
        header: t('links.columns.title'),
        cell: (row) => row.title,
      },
      {
        id: 'fund',
        header: t('links.columns.fund'),
        cell: (row) => {
          const name = row.defaultFundName?.trim();
          if (!name) return '—';
          const extra = Math.max(0, (row.allowedFundIds?.length ?? 0) - 1);
          return extra > 0 ? t('links.fundWithMore', { fund: name, count: extra }) : name;
        },
      },
      {
        id: 'status',
        header: t('links.columns.status'),
        cell: (row) => (
          <Badge variant={row.isActive ? 'success' : 'neutral'}>
            {row.isActive ? t('links.status.active') : t('links.status.inactive')}
          </Badge>
        ),
      },
      {
        id: 'gifts',
        header: t('links.columns.gifts'),
        cell: (row) => formatNumber(row.giftCount),
        hideBelowMd: true,
      },
      {
        id: 'views',
        header: t('links.columns.views'),
        cell: (row) => formatNumber(row.viewCount),
        hideBelowMd: true,
      },
    ],
    [t],
  );

  const rows = list.data?.data ?? [];
  const shareUrl =
    shareLink && church?.slug ? buildGivingLinkUrl(church.slug, shareLink.slug) : '';

  return (
    <div className="space-y-4">
      {canManage && !paymentReady ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('links.requiresConnectionTitle')}</CardTitle>
            <CardDescription>
              {canConfigurePayments
                ? t('links.requiresConnectionDescription')
                : t('links.requiresConnectionAdmin')}
            </CardDescription>
          </CardHeader>
          {canConfigurePayments ? (
            <CardFooter className="flex justify-end">
              <Button asChild>
                <Link to={ONLINE_GIVING_SETTINGS_PATH}>{t('links.requiresConnectionAction')}</Link>
              </Button>
            </CardFooter>
          ) : null}
        </Card>
      ) : null}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <p className="text-center text-body-sm text-fg-secondary">{t('links.empty')}</p>
        }
        onRowClick={(row) => openDetail(row.id, 'view')}
        rowActions={(row) => (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={!church?.slug}
              onClick={() => setShareLink(row)}
            >
              {t('links.share')}
            </Button>
            {canManage ? (
              <>
                <Button size="sm" variant="outline" onClick={() => openDetail(row.id, 'edit')}>
                  {t('links.detail.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={setActive.isPending}
                  onClick={() =>
                    setActive.mutate({ id: row.id, isActive: !row.isActive })
                  }
                >
                  {row.isActive ? t('links.pause') : t('links.resume')}
                </Button>
              </>
            ) : null}
          </div>
        )}
        actionsHeader={t('links.columns.actions')}
      />

      <GivingLinkDetailDialog
        linkId={detailLinkId}
        open={Boolean(detailLinkId)}
        onOpenChange={(open) => {
          if (!open) setDetailLinkId(null);
        }}
        canEdit={canManage}
        initialMode={detailMode}
        onShare={(link) => setShareLink(link)}
      />

      <ShareDialog
        open={Boolean(shareLink)}
        onOpenChange={(open) => {
          if (!open) setShareLink(null);
        }}
        url={shareUrl}
        title={t('links.shareTitle')}
        description={t('links.shareDescription')}
        downloadName={shareLink?.slug ?? 'giving-link'}
        shareTitle={shareLink?.title}
        branding={
          church
            ? {
                churchName: church.name,
                campusName: church.campusName,
                logoUrl: church.logoUrl,
              }
            : undefined
        }
        labels={{
          copy: tCommon('action.copy'),
          copied: tCommon('action.copied'),
          share: t('links.share'),
          download: t('links.downloadQr'),
          close: tCommon('action.close'),
          qrLabel: t('links.qrLabel'),
        }}
      />
    </div>
  );
};
